'use strict';

// Vue
import Vue from 'vue';

// Services
import { arrayLoop } from 'utils/array';
import { modelToView, hasError, browserSupportsInputDate, getFormData, separator } from 'utils/forms';

// State
const state = {
  separator,
  forms: {},
  serverErrors: {},
  fields: {},
  browserSupportsInputDate: browserSupportsInputDate(),
  files: [],
};

// Getters
const getters = {
  separator: state => state.separator,
  browserSupportsInputDate: state => state.browserSupportsInputDate,

  // Field
  getField: state => id => state.fields[id] || null,
  getFieldModel: state => id => state.fields[id] ? state.fields[id].model : undefined,
  getFieldView: state => id => state.fields[id] ? state.fields[id].view : null,
  getFieldError: state => id => state.fields[id] ? state.fields[id].error : '',
  getFieldDirty: state => id => state.fields[id] ? state.fields[id].dirty : false,
  getFieldTelPrefix: state => id => state.fields[id] ? (state.fields[id].telPrefix || null) : null,
  getFieldServerError: state => id => state.fields[id] ? state.fields[id].serverError: '',
  getFiles: state => state.files,

  // Form
  getForm: state => id => {
    const form = state.forms[id];

    if (!form) return null;

    const formData = getFormData(id, state.fields);

    formData.state = form;

    return formData;
  },
  getFormView: state => id => !state.forms[id] ? null : getFormData(id, state.fields, { removeFormId: true }).view,
  getFormServerError: state => id => !state.serverErrors[id] || !state.forms[id] ? null : state.serverErrors[id],
};

// Mutations
const mutations = {
  CREATE_FORM (state, id) {
    Vue.set(state.forms, id, 'idle');
  },
  RESET_FORM (state, id) {
    if (!state.forms[id]) return;
    state.forms[id] = 'idle';
  },
  DESTROY_FORM (state, id) {
    Vue.set(state.forms, id, null);
  },
  CREATE_FIELD (state, { id, type, required, options, min, max, matches, valid, telPrefix }) {
    const formId = id.split(state.separator)[0];

    const field = {
      type,
      formId,
      model: undefined,
      view: undefined,
      dirty: false,
      error: '',
      serverError: '',
      valid,
      required,
      min,
      max,
      matches,
      telPrefix,
    };

    if (options) {
      field.options = {};

      if (type === 'select') {
        field.model = '-1';
      }

      arrayLoop(options, (i, option) => {
        const optionIsObject = typeof(option) === 'object';

        const optionLabel = optionIsObject ? option.label : option;
        const optionValue = optionIsObject ? option.value : option;
        const optionType = optionIsObject && option.type ? option.type : 'text';

        field.options[i] = {
          label: optionLabel,
          value: optionValue,
          type: optionType,
        };
      });
    }

    Vue.set(state.fields, id, field);
  },
  DESTROY_FIELD (state, id) {
    Vue.set(state.fields, id, null);
  },
  SET_FIELD_MODEL (state, { id, value }) {
    const field = state.fields[id];

    const newField = {
      ...field,
      model: value,
      view: modelToView(value, state.fields[id]),
      serverError: '', // WHY: Resets the serverError when an input is changed
    };

    newField.error = hasError(id, newField) || '';

    Vue.set(state.fields, id, newField);
  },
  SET_ERROR (state, { id, error }) {
    Vue.set(state.fields[id], 'error', error);
  },
  SET_FIELD_DIRTY (state, id) {
    Vue.set(state.fields[id], 'dirty', true);
  },
  SET_STATE (state, { id, value }) {
    if (!state.forms[id]) return;
    Vue.set(state.forms, id, value);
  },
  SET_TEL_PREFIX(state, { id, prefix }) {
    if (!state.fields[id]) return;

    Vue.set(state.fields[id], 'telPrefix', prefix);
  },
  SET_FORM_SERVER_ERROR(state, { id, error }) {
    Vue.set(state.serverErrors, id, error || null);
  },
  SET_FIELD_SERVER_ERROR(state, { id, error }) {
    if (!state.fields[id]) return;

    Vue.set(state.fields[id], 'serverError', error);
  },
  SAVE_FILE(state, file)  {
    state.files.push(file);
  },
  REMOVE_FILE(state, file) {
    state.files.splice(file, 1);
  },
  CLEAR_FILES(state, payload) {
    Vue.set(state, 'files', payload );
  },
};

// Actions
const actions = {
  setupForm({ commit, state, dispatch }, id) {
    if (!id) {
      console.error('No form ID defined – store/setupForm');
      return false;
    }

    const form = state.forms[id] || null;

    if (form === null) {
      commit('CREATE_FORM', id);
    } else {
      commit('RESET_FORM', id);
    }

    dispatch('setFormServerError', {
      id,
    });
  },
  destroyForm({ commit, dispatch }, id) {
    const form = state.forms[id] || null;

    if (form === null) {
      console.warn(`No form with ID ${id} exists – store/destroyForm`);
      return false;
    }

    commit('DESTROY_FORM', id);

    dispatch('setFormServerError', {
      id,
    });
  },
  setupField({ commit, dispatch, state }, { id, type, required, options, value, min, max, matches, telPrefix }) {
    if (!id) {
      console.error('No field ID defined – store/setupField');
      return false;
    }

    const field = state.fields[id];

    if (!field) {

      if (!telPrefix) {
        telPrefix = type === 'tel' ? '+1' : undefined;
      }

      commit('CREATE_FIELD', {
        id,
        type,
        required,
        options,
        telPrefix,
        value,
        min,
        max,
        matches,
      });

      if (value || required) {
        dispatch('updateField', {
          id,
          value,
        });
      }
    }
  },
  destroyField({ commit }, id) {
    commit('DESTROY_FIELD', id);
  },
  updateField({ commit, dispatch, state }, { id, value }) {
    if (!state.fields[id]) {
      console.warn(`No field with ID ${id} exists – store/updateField`);
      return false;
    }

    commit('SET_FIELD_MODEL', {
      id,
      value,
    });

    const formId = id.split(state.separator)[0];

    if (formId !== 'global') {

      dispatch('setFormServerError', {
        id: formId,
      });

      const form = state.forms[formId] || {};
      arrayLoop(form.fields || {}, (fieldId) => {
        const field = state.fields[fieldId];

        if (!field || !field.matches) return;

        const matchingField = state.fields[`${formId}${state.separator}${field.matches}`] || {};

        if (field.error && field.error !== 'matches') return;

        commit('SET_ERROR', {
          id: fieldId,
          error: (matchingField.model !== 'undefined' && field.model !== matchingField.model) ? 'matches' : '',
        });
      });
    }
  },
  setFieldDirty({ commit, state }, id) {
    if (!state.fields[id]) {
      console.warn(`No field with ID ${id} exists – store/setFieldDirty`);
      return false;
    }

    const field = state.fields[id];

    commit('SET_FIELD_DIRTY', id);

    if (field.error !== 'matches') {
      commit('SET_ERROR', {
        id,
        error: hasError(id, field),
      });
    }
  },
  setFormState({ commit }, { id, value }) {
    if (!state.forms[id]) {
      console.error(`No form with ID ${id} exists – store/setFormState`);
      return false;
    }

    commit('SET_STATE', {
      id,
      value: value || 'idle',
    });
  },
  setTelPrefix({ commit }, { id, prefix }) {
    if (!state.fields[id]) {
      console.warn(`No field with ID ${id} exists – store/setTelPrefix`);
      return false;
    }

    commit('SET_TEL_PREFIX', { id, prefix });
  },
  setFormServerError({ commit }, { id, error }) {
    commit('SET_FORM_SERVER_ERROR', {
      id,
      error,
    });
  },
  setFieldServerError({ commit }, { id, formId, error }) {
    id = `${formId}${separator}${id}`;

    if (!state.fields[id]) {
      console.warn(`No field with ID ${id} exists – store/setTelPrefix`);
      return false;
    }

    commit('SET_FIELD_SERVER_ERROR', {
      id,
      error,
    });
  },
  setFormDirty({ dispatch, getters }, id) {
    return new Promise(resolve => {
      const form = getters.getForm(id);

      if (!form) {
        console.warn(`No form with ID ${id} exists – store/setFormDirty`);
        return false;
      }

      let scrollTo = '';

      arrayLoop(form.errors || [], (fieldId, error) => {
        if (!error) return;

        if (!scrollTo) scrollTo = fieldId;
        dispatch('setFieldDirty', fieldId);
      });

      resolve(scrollTo);
    });
  },

  saveFile({ commit }, file) {
    commit('SAVE_FILE', file);
  },

  removeFile({ commit }, file) {
    commit('REMOVE_FILE', file);
  },

  clearFiles({ commit }) {
    commit('CLEAR_FILES', []);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};



// WEBPACK FOOTER //
// ./client/store/forms.js